'use client'

import { cn } from '@workspace/ui/lib/utils'
import { cva } from 'class-variance-authority'
import { Check, ChevronDown, Mic, MicOff, Video, VideoOff } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu'

const toggleButtonVariants = cva(
  'flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  {
    variants: {
      enabled: {
        true: 'bg-secondary hover:bg-muted',
        false: 'bg-destructive hover:bg-destructive/90',
      },
      size: {
        sm: 'size-8',
        md: 'size-10',
        lg: 'size-12',
      },
    },
    defaultVariants: {
      enabled: true,
      size: 'md',
    },
  }
)

const dropdownButtonVariants = cva(
  'flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  {
    variants: {
      enabled: {
        true: 'bg-secondary/80 hover:bg-muted',
        false: 'bg-destructive/80 hover:bg-destructive/70',
      },
      size: {
        sm: 'h-8 w-5',
        md: 'h-10 w-6',
        lg: 'h-12 w-7',
      },
    },
    defaultVariants: {
      enabled: true,
      size: 'md',
    },
  }
)

const iconSizeMap = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
}

export type MediaToggleType = 'audio' | 'video'

const typeIcons: Record<MediaToggleType, { enabled: typeof Mic; disabled: typeof MicOff }> = {
  audio: { enabled: Mic, disabled: MicOff },
  video: { enabled: Video, disabled: VideoOff },
}

const typeLabels: Record<MediaToggleType, { enabled: string; disabled: string }> = {
  audio: { enabled: 'Mute microphone', disabled: 'Unmute microphone' },
  video: { enabled: 'Turn off camera', disabled: 'Turn on camera' },
}

export interface MediaToggleDropdownProps {
  type: MediaToggleType
  enabled: boolean
  onToggle: () => void
  devices: MediaDeviceInfo[]
  selectedDeviceId: string
  onDeviceSelect: (deviceId: string) => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

function MediaToggleDropdown({
  type,
  enabled,
  onToggle,
  devices,
  selectedDeviceId,
  onDeviceSelect,
  size = 'md',
  className,
}: MediaToggleDropdownProps) {
  const icons = typeIcons[type]
  const labels = typeLabels[type]
  const Icon = enabled ? icons.enabled : icons.disabled
  const label = enabled ? labels.enabled : labels.disabled
  const iconSize = iconSizeMap[size]

  return (
    <div className={cn('flex', className)}>
      {/* Main toggle button */}
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={enabled}
        aria-label={label}
        className={cn(toggleButtonVariants({ enabled, size }), 'rounded-l-lg')}
      >
        <Icon className={cn(iconSize, 'text-white')} />
      </button>

      {/* Dropdown trigger */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Select ${type} device`}
            className={cn(
              dropdownButtonVariants({ enabled, size }),
              'rounded-r-lg border-l border-white/20'
            )}
          >
            <ChevronDown className="size-3 text-white" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="min-w-48">
          {devices.length === 0 ? (
            <DropdownMenuItem disabled>No devices found</DropdownMenuItem>
          ) : (
            devices.map((device) => (
              <DropdownMenuItem
                key={device.deviceId}
                onClick={() => onDeviceSelect(device.deviceId)}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate">{device.label || `${type} device`}</span>
                {device.deviceId === selectedDeviceId && <Check className="size-4 text-primary" />}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export { MediaToggleDropdown }
